import React, { createContext, useContext, useMemo } from 'react';
import { FilterContext, useResourceContext } from 'react-admin';
import { isFunction } from 'lodash';

interface FiltersContextType {
  hasFilterSidebar?: boolean;
}

const FilterSidebarContext = createContext<FiltersContextType>({
  hasFilterSidebar: false
});

function useIsEnabledSidebarFilter(): boolean {
  return useContext(FilterSidebarContext)?.hasFilterSidebar ?? false;
}

interface ChipItemProps {
  source: string;
  value: unknown;
}

function useGetChipValue({ source, value }: ChipItemProps): JSX.Element | null {
  const filters = useContext(FilterContext);
  const resource = useResourceContext();

  const currentSourceFilter = useMemo(() => {
    return filters.find(
      (filter) =>
        React.isValidElement(filter) && (filter.props?.source === source || source?.includes(filter.props?.source))
    );
  }, [filters, source]);
  const currentSourceFilterProps = useMemo(
    () => (React.isValidElement(currentSourceFilter) ? currentSourceFilter.props : {}),
    [currentSourceFilter]
  );

  if (!currentSourceFilterProps?.chip) {
    throw new Error(
      `No chip component found for filter source "${source}". Ensure your filter components define a 'chip' prop.`
    );
  }

  const chipProps = {
    ...currentSourceFilterProps,
    specificSource: source,
    record: { [source]: value },
    value,
    resource
  };

  const Chip = React.isValidElement(currentSourceFilterProps.chip)
    ? React.cloneElement(currentSourceFilterProps.chip, chipProps)
    : isFunction(currentSourceFilterProps.chip)
    ? React.createElement(currentSourceFilterProps.chip, chipProps)
    : null;

  return Chip;
}

export { FilterSidebarContext, useGetChipValue, useIsEnabledSidebarFilter };
